import React from "react";
import { Link } from "react-router-dom";
import { Logo } from "../Logo";
import { useLanguage } from "../../contexts/LanguageContext";
import { OFFICES } from "../../lib/offices";
import { reopenCookieBanner } from "../CookieBanner";

export const Footer = () => {
  const { t, lang } = useLanguage();

  const isFr = lang === "fr";
  const legalPath = isFr ? "/mentions-legales" : "/legal-notice";
  const privacyPath = isFr ? "/politique-confidentialite" : "/privacy-policy";

  const handleNav = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0D2F4C] text-white pt-24 pb-10"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 pb-16 border-b border-white/10">
          {/* Brand */}
          <div className="md:col-span-5">
            <Logo variant="gold" />
            <p
              className="font-serif italic text-2xl text-white mt-8 leading-tight"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {t.footer.tagline}
            </p>
            <p className="text-sm text-white/60 mt-4 max-w-sm leading-relaxed">
              {t.footer.description}
            </p>
          </div>

          {/* Navigation */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">
              {t.footer.nav_title}
            </h4>
            <ul className="space-y-3">
              {[
                { id: "about", label: t.nav.about },
                { id: "services", label: t.nav.services },
                { id: "presence", label: t.nav.presence },
                { id: "news", label: t.nav.news },
                { id: "contact", label: t.nav.contact },
              ].map((l) => (
                <li key={l.id}>
                  <button
                    data-testid={`footer-nav-${l.id}`}
                    onClick={() => handleNav(l.id)}
                    className="text-sm text-white/80 hover:text-white transition-colors"
                  >
                    {l.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Offices */}
          <div className="md:col-span-3">
            <h4 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">
              {t.nav.presence}
            </h4>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2">
              {OFFICES.map((o) => (
                <li key={o.key} className="text-sm text-white/70">
                  {o.name}
                </li>
              ))}
            </ul>
          </div>

          {/* Contact */}
          <div className="md:col-span-2">
            <h4 className="text-xs uppercase tracking-[0.25em] text-white/50 mb-5">
              {t.footer.contact_title}
            </h4>
            <ul className="space-y-4 text-sm">
              <li>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961] mb-1">
                  {t.contact.info_title}
                </div>
                <div className="text-white/80">{t.contact.address}</div>
              </li>
              <li>
                <div className="text-[10px] uppercase tracking-[0.2em] text-[#C9A961] mb-1">
                  {t.contact.dakar_title}
                </div>
                <div className="text-white/80 leading-relaxed">
                  {t.contact.dakar_lines.map((line, i) => (
                    <div key={i}>{line}</div>
                  ))}
                </div>
              </li>
              <li>
                <a
                  href="mailto:info@richildinvest.com"
                  className="text-white/80 hover:text-white transition-colors"
                  data-testid="footer-email"
                >
                  info@richildinvest.com
                </a>
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom */}
        <div className="pt-8 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="text-xs text-white/50">
            © {new Date().getFullYear()} Richild Invest GBC · {t.footer.rights}
          </div>
          <div className="flex flex-wrap gap-6 text-xs">
            <Link
              to={privacyPath}
              data-testid="footer-privacy"
              className="text-white/50 hover:text-white transition-colors"
            >
              {t.footer.privacy}
            </Link>
            <Link
              to={legalPath}
              data-testid="footer-legal-notice"
              className="text-white/50 hover:text-white transition-colors"
            >
              {isFr ? "Mentions légales" : "Legal notice"}
            </Link>
            <button
              data-testid="footer-cookies"
              onClick={reopenCookieBanner}
              className="text-white/50 hover:text-white transition-colors"
            >
              {t.footer.cookies}
            </button>
          </div>
        </div>
      </div>
    </footer>
  );
};
